import { create } from 'zustand';
import { produce } from 'immer';

import _merge from 'lodash/merge';
import _set from 'lodash/set';
import _setWith from 'lodash/setWith';
import _uniqueId from 'lodash/uniqueId';

import {
  TableId,
  TableInputCellData,
  TableInputColumn,
  TableInputData,
  TableInputDataByPage,
  TableInputDelete,
  TableInputDeletes,
  TableInputDeleteExclusion,
  TableInputOnChangeData,
  TableInputQuestionData,
  TableInputRowData,
  TableInputUpdate,
  TableInputUpdates,
} from '@components/table-input/tableInput.types';
import { sanitizeText } from '@utils/helpers';
import { SelectInputOption } from '@api/types';

interface TableInputStoreValues {
  tableInputAddedCount: Record<TableId, number>;
  tableInputData: TableInputData;
  tableInputDeletes: TableInputDeletes;
  tableInputDeleteExclusions: Record<TableId, TableInputDeleteExclusion>;
  tableInputForceOverwrite: Record<TableId, boolean>;
  tableInputSelectAllMode: Record<TableId, boolean>;
  tableInputUpdates: TableInputUpdates;
}

interface TableInputState extends TableInputStoreValues {
  addTableInputRow: (tableId: TableId, newRow: TableInputRowData) => void;
  decrementTableInputAddedRowCount: (tableId: TableId) => void;
  hasTableInputChanges: (tableId: TableId) => boolean;
  hasTableInputDeletes: (tableId: TableId) => boolean;
  incrementTableInputAddedRowCount: (tableId: TableId) => void;
  resetTableInput: (tableId: TableId) => void;
  resetTableInputEdits: (tableId: TableId) => void;

  getTableInputAddedRowCount: (tableId: TableId) => number;
  getTableInputBlankRow: (tableId: TableId) => TableInputCellData[];
  getTableInputColumnData: (tableId: TableId) => TableInputColumn[] | undefined;
  getTableInputData: (tableId: TableId) => TableInputQuestionData | undefined;
  getTableInputDeletes: (tableId: TableId) => TableInputDelete | undefined;
  getTableInputDeleteExclusions: (tableId: TableId) => TableInputDeleteExclusion | undefined;
  getTableInputForceOverwrite: (tableId: TableId) => boolean | false;
  getTableInputItemsPerPage: (tableId: TableId) => number;
  getTableInputPageData: (tableId: TableId, page: number) => TableInputRowData[];
  getTableInputPageKeys: (tableId: TableId) => number[];
  getTableInputPageNumber: (tableId: TableId) => number;
  getTableInputRowCount: (tableId: TableId) => number;
  getTableInputSelectAllMode: (tableId: TableId) => boolean | false;
  getTableInputSortColumn: (tableId: TableId) => string;
  getTableInputSortDirection: (tableId: TableId) => 'asc' | 'desc';
  getTableInputUpdates: (tableId: TableId) => TableInputUpdate | undefined;

  setTableInputCellError: (params: {
    tableId: TableId;
    pageKey: number;
    rowIndex: number;
    colIndex: number;
    errorMessage: string | undefined;
  }) => void;
  setTableInputCellOptions: (params: {
    tableId: TableId;
    pageKey: number;
    rowIndex: number;
    colIndex: number;
    options: SelectInputOption[];
  }) => void;
  setTableInputAnswer: (data: TableInputOnChangeData, tableId: TableId) => void;
  setTableInputColumnData: (tableId: TableId, columnData: TableInputColumn[]) => void;
  setTableInputColumnOptions: (params: { tableId: TableId; colIndex: number; options: SelectInputOption[] }) => void;
  setTableInputDataByPage: (tableId: TableId, data: TableInputDataByPage, overwriteNewRecords?: boolean) => void;
  setTableInputForceOverwrite: (tableId: TableId, forceOverwrite: boolean) => void;
  setTableInputItemsPerPage: (tableId: TableId, itemsPerPage: number) => void;
  setTableInputPageNumber: (tableId: TableId, pageNumber: number) => void;
  setTableInputRowCount: (tableId: TableId, count: number) => void;
  setTableInputSelectAllMode: (tableId: TableId, isEnabled: boolean) => void;
  setTableInputSortColumn: (tableId: TableId, sortColumn: string) => void;
  setTableInputSortDirection: (tableId: TableId, sortDirection: 'asc' | 'desc') => void;
}

export const newTableInputRecordIdPrefix = 'new-record-';

export const useTableInput = create<TableInputState>((set, get) => ({
  tableInputAddedCount: {},
  tableInputData: {},
  tableInputDeletes: {},
  tableInputDeleteExclusions: {},
  tableInputForceOverwrite: {},
  tableInputSelectAllMode: {},
  tableInputUpdates: {},

  addTableInputRow: async (tableId, newRow) => {
    const itemsPerPage = get().getTableInputItemsPerPage(tableId);
    const totalRowCount = get().getTableInputRowCount(tableId);
    const addedRowCount = get().getTableInputAddedRowCount(tableId);
    const numColumns = get().getTableInputColumnData(tableId)?.length || 0;
    const savedRowCount = totalRowCount - addedRowCount;
    const lastPageNumberWithSavedRecords = Math.ceil(savedRowCount / itemsPerPage);

    set(
      produce((draft) => {
        const newRecordPageNumber = Math.floor(totalRowCount / itemsPerPage) + 1;
        const isPageLoaded = !!get().tableInputData?.[tableId]?.pagedData?.[newRecordPageNumber];
        const numSavedRecordsOnPage =
          newRecordPageNumber === lastPageNumberWithSavedRecords ? savedRowCount % itemsPerPage : 0;

        if (isPageLoaded) {
          // if the target page is already loaded, append the new row to the existing rows on that page
          draft.tableInputData[tableId].pagedData[newRecordPageNumber].rowData.push(newRow);
        } else if (numSavedRecordsOnPage === 0) {
          // if the target page is not loaded, but the new row is the first row on the page, create the new page with only the new row
          draft.tableInputData[tableId].pagedData[newRecordPageNumber] = { rowData: [newRow] };
        } else {
          // if the target page is not yet loaded from the API, and the new row is not the first row on the page, create a new page with placeholders for the saved rows, then append the new row to the end
          // when page data is loaded from API after this function completes, it will overwrite the placeholder rows, but the new row will remain

          const emptyRow = Array.from({ length: numColumns }, () => ({}));
          const newPageData = Array.from({ length: numSavedRecordsOnPage }, () => emptyRow);
          newPageData[numSavedRecordsOnPage] = newRow;

          draft.tableInputData[tableId].pagedData[newRecordPageNumber] = { rowData: newPageData };
        }
      })
    );

    // increment added row count
    get().incrementTableInputAddedRowCount(tableId);
  },

  decrementTableInputAddedRowCount: (tableId) => {
    set(
      produce((draft) => {
        const currentCount = draft.tableInputAddedCount?.[tableId] || 0;
        _set(draft.tableInputAddedCount, [tableId], Math.max(currentCount - 1, 0));
      })
    );
  },

  getTableInputForceOverwrite: (tableId) => get().tableInputForceOverwrite?.[tableId] || false,

  getTableInputAddedRowCount: (tableId) => get().tableInputAddedCount?.[tableId] || 0,

  getTableInputBlankRow: (tableId) => {
    const tableColumnData = get().getTableInputColumnData(tableId);
    const totalRowCount = get().getTableInputRowCount(tableId);

    if (!tableColumnData) {
      return [];
    }

    const blankRow: TableInputCellData[] = [];
    const recordId = _uniqueId(newTableInputRecordIdPrefix);

    tableColumnData?.forEach((column) => {
      blankRow.push({
        columnId: column.columnId as number,
        recordId,
        rowNumber: totalRowCount + 1,
        typeId: column.questionTypeId as number,
        value: null,
      });
    });

    return blankRow;
  },

  getTableInputColumnData: (tableId) => get().tableInputData?.[tableId]?.columnData || [],

  getTableInputData: (tableId) => {
    const data = get().tableInputData?.[tableId];

    if (!data) {
      set(
        produce((draft) => {
          _set(draft.tableInputData, [tableId], {});
        })
      );
    }

    return get().tableInputData?.[tableId];
  },

  getTableInputDeletes: (tableId) => {
    const deletes = get().tableInputDeletes?.[tableId];

    if (!deletes) {
      set(
        produce((draft) => {
          _set(draft.tableInputDeletes, [tableId], {});
        })
      );
    }

    return get().tableInputDeletes?.[tableId];
  },

  getTableInputDeleteExclusions: (tableId) => {
    const exclusions = get().tableInputDeleteExclusions?.[tableId];

    if (!exclusions) {
      set(
        produce((draft) => {
          _set(draft.tableInputDeleteExclusions, [tableId], {});
        })
      );
    }

    return get().tableInputDeleteExclusions?.[tableId];
  },

  getTableInputItemsPerPage: (tableId) => get().tableInputData?.[tableId]?.itemsPerPage || 0,

  getTableInputPageData: (tableId, page) => get().tableInputData?.[tableId]?.pagedData?.[page]?.rowData || [],

  getTableInputPageKeys: (tableId) => {
    const pagedData = get().tableInputData?.[tableId]?.pagedData || {};
    return Object.keys(pagedData).map((key) => Number(key));
  },

  getTableInputPageNumber: (tableId) => get().tableInputData?.[tableId]?.currentPage || 1,

  getTableInputRowCount: (tableId) =>
    (get().tableInputData?.[tableId]?.rowCount || 0) + (get().tableInputAddedCount?.[tableId] || 0),

  getTableInputSelectAllMode: (tableId) => get().tableInputSelectAllMode?.[tableId] || false,

  getTableInputSortColumn: (tableId) => get().tableInputData?.[tableId]?.sortColumn || '',

  getTableInputSortDirection: (tableId) => get().tableInputData?.[tableId]?.sortDirection || 'asc',

  getTableInputUpdates: (tableId) => {
    const updates = get().tableInputUpdates?.[tableId];

    if (!updates) {
      set(
        produce((draft) => {
          _set(draft.tableInputUpdates, [tableId], {});
        })
      );
    }

    return get().tableInputUpdates?.[tableId];
  },

  hasTableInputChanges: (tableId) => {
    const addedRowCount = get().getTableInputAddedRowCount(tableId);
    const updates = Object.keys(get().getTableInputUpdates(tableId) || {}).length;

    return addedRowCount > 0 || updates > 0;
  },

  hasTableInputDeletes: (tableId) => {
    const deletes = Object.keys(get().getTableInputDeletes(tableId) || {}).length;
    const deleteAllSelected = get().getTableInputSelectAllMode(tableId);

    return deletes > 0 || deleteAllSelected;
  },

  incrementTableInputAddedRowCount: (tableId) => {
    set(
      produce((draft) => {
        const currentCount = draft.tableInputAddedCount?.[tableId] || 0;
        _set(draft.tableInputAddedCount, [tableId], currentCount + 1);
      })
    );
  },

  resetTableInput: (tableId) => {
    set(
      produce((draft) => {
        if (draft.tableInputData[tableId]) {
          draft.tableInputData[tableId].pagedData = {};
        }
        if (draft.tableInputDeletes[tableId]) {
          draft.tableInputDeletes[tableId] = {};
        }
        if (draft.tableInputUpdates[tableId]) {
          draft.tableInputUpdates[tableId] = {};
        }
        if (draft.tableInputAddedCount[tableId]) {
          draft.tableInputAddedCount[tableId] = 0;
        }
        if (draft.tableInputDeleteExclusions[tableId]) {
          draft.tableInputDeleteExclusions[tableId] = {};
        }
        if (draft.tableInputSelectAllMode[tableId]) {
          draft.tableInputSelectAllMode[tableId] = false;
        }
      })
    );
  },

  resetTableInputEdits: (tableId) => {
    set(
      produce((draft) => {
        draft.tableInputDeletes[tableId] = {};
        draft.tableInputUpdates[tableId] = {};
        draft.tableInputAddedCount[tableId] = 0;
      })
    );
  },

  setTableInputAnswer: (data, tableId) => {
    const { action } = data;

    if (action === 'update') {
      const { colIndex = 0, recordId = 0, value } = data;
      const cleanValue = typeof value === 'string' ? sanitizeText(value).trim() : value;

      set(
        produce((draft) => {
          _setWith(draft.tableInputUpdates, [tableId, recordId, colIndex], cleanValue, Object);
        })
      );
    } else if (action === 'delete') {
      const isInSelectAllMode = get().tableInputSelectAllMode?.[tableId];
      const { recordId = 0 } = data;

      // ensure that any recorded updates for this row are removed before marking it as deleted
      set(
        produce((draft) => {
          delete draft.tableInputUpdates?.[tableId]?.[recordId];
        })
      );

      set(
        produce((draft) => {
          if (isInSelectAllMode) {
            delete draft.tableInputDeleteExclusions[tableId][recordId];
          } else {
            _setWith(draft.tableInputDeletes, [tableId, recordId], true, Object);
          }
        })
      );
    } else if (action === 'undo-delete') {
      const isInSelectAllMode = get().tableInputSelectAllMode?.[tableId];
      const { recordId = 0 } = data;

      if (isInSelectAllMode) {
        set(
          produce((draft) => {
            _setWith(draft.tableInputDeleteExclusions, [tableId, recordId], true, Object);
          })
        );
      } else {
        set(
          produce((draft) => {
            delete draft.tableInputDeletes[tableId][recordId];
          })
        );
      }
    } else if (action === 'add') {
      const { newRow = [] } = data;
      get().addTableInputRow(tableId, newRow);
    }
  },

  setTableInputCellError: ({ tableId, pageKey, rowIndex, colIndex, errorMessage }) => {
    set(
      produce((draft) => {
        const cell = draft.tableInputData?.[tableId]?.pagedData?.[pageKey]?.rowData?.[rowIndex]?.[colIndex];

        if (cell) {
          cell.errorText = errorMessage;
        }
      })
    );
  },

  setTableInputCellOptions: ({ tableId, pageKey, rowIndex, colIndex, options }) => {
    set(
      produce((draft) => {
        const cell = draft.tableInputData?.[tableId]?.pagedData?.[pageKey]?.rowData?.[rowIndex]?.[colIndex];
        if (cell) {
          cell.options = options;
        }
      })
    );
  },

  setTableInputColumnData: (tableId, columnData) => {
    set(
      produce((draft) => {
        draft.tableInputData[tableId] = {
          columnData,
          pagedData: {},
        };
      })
    );
  },

  setTableInputColumnOptions: ({ tableId, colIndex, options }) => {
    set(
      produce((draft) => {
        const column = draft.tableInputData?.[tableId]?.columnData?.[colIndex];
        if (column) {
          column.options = options;
        }
      })
    );
  },

  setTableInputDataByPage: (tableId, updatedData) => {
    // overwrite all data with incoming data if forceOverwrite is true
    const forceOverwrite = get().getTableInputForceOverwrite(tableId);
    const mergedData = forceOverwrite ? updatedData : _merge({}, get().tableInputData[tableId].pagedData, updatedData);
    const pageNumber = get().getTableInputPageNumber(tableId);

    set(
      produce((draft) => {
        draft.tableInputData[tableId].pagedData = mergedData;
      })
    );

    // if forcing an overwrite and no records remain on the current page, set page to the last page with records
    if (forceOverwrite && mergedData[pageNumber].rowData.length === 0) {
      const totalRowCount = get().getTableInputRowCount(tableId);
      const itemsPerPage = get().tableInputData[tableId].itemsPerPage;
      const lastPageIndex = Math.ceil(totalRowCount / itemsPerPage);

      get().setTableInputPageNumber(tableId, lastPageIndex);
    }

    // reset the forceOverwrite flag after updating the data
    get().setTableInputForceOverwrite(tableId, false);
  },

  setTableInputForceOverwrite: (tableId, forceOverwrite) => {
    set(
      produce((draft) => {
        _set(draft.tableInputForceOverwrite, [tableId], forceOverwrite);
      })
    );
  },

  setTableInputItemsPerPage: (tableId, itemsPerPage) => {
    set(
      produce((draft) => {
        _set(draft.tableInputData, [tableId, 'itemsPerPage'], itemsPerPage);
      })
    );
  },

  setTableInputPageNumber: (tableId, pageNumber) => {
    set(
      produce((draft) => {
        _set(draft.tableInputData, [tableId, 'currentPage'], pageNumber);
      })
    );
  },

  setTableInputRowCount: (tableId, count) => {
    set(
      produce((draft) => {
        _set(draft.tableInputData, [tableId, 'rowCount'], count);
      })
    );
  },

  setTableInputSelectAllMode: (tableId, isSelectAllEnabled) => {
    set(
      produce((draft) => {
        _set(draft.tableInputSelectAllMode, [tableId], isSelectAllEnabled);
      })
    );
    if (isSelectAllEnabled) {
      const tableData = get().tableInputData[tableId].pagedData;
      const allRows = Object.values(tableData || {}).flatMap((page) => page.rowData);
      const allRowIds = allRows.map((row) => row[0].recordId);
      const allDeletes = allRowIds.reduce<TableInputDelete>((acc, rowId) => {
        acc[rowId] = true;
        return acc;
      }, {});

      set(
        produce((draft) => {
          _set(draft.tableInputDeletes, [tableId], allDeletes);
          _set(draft.tableInputDeleteExclusions, [tableId], {});
        })
      );
    } else {
      set(
        produce((draft) => {
          _set(draft.tableInputDeletes, [tableId], {});
        })
      );
    }
  },

  setTableInputSortColumn: (tableId, sortColumn) => {
    set(
      produce((draft) => {
        _set(draft.tableInputData, [tableId, 'sortColumn'], sortColumn);
      })
    );
  },

  setTableInputSortDirection: (tableId, sortDirection) => {
    set(
      produce((draft) => {
        _set(draft.tableInputData, [tableId, 'sortDirection'], sortDirection);
      })
    );
  },
}));
